import React, { useEffect, useState } from 'react';
import { CreditCard, Sparkles, X } from 'lucide-react';
import { Button } from "./ui/button"
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./ui/card"
import { logEvent } from '../lib/amplitude';
import { useNavigate } from 'react-router-dom';

const TRIAL_PLAN = 'Musician';

const TOPUP_TOKENS = 50;
const TOPUP_PRICE = '4.99';

function OutOfCreditsDialog({ isOpen, onClose, onPurchase, onTopUp, isSubscriptionActive, trialEligible, hasTrack }) {
  const navigate = useNavigate();
  const [isStarting, setIsStarting] = useState(false);
  const offerTrial = !isSubscriptionActive && trialEligible;

  const handleTopUp = () => {
    logEvent('Out of Credits Dialog Action Clicked', { isSubscriptionActive, action: 'top_up' });
    onTopUp();
  };

  useEffect(() => {
    if (isOpen) {
      logEvent('Out of Credits Dialog Opened', { isSubscriptionActive, offerTrial });
    }
  }, [isOpen, isSubscriptionActive, offerTrial]);

  if (!isOpen) return null;

  const title = isSubscriptionActive
    ? "Out of Message Tokens"
    : offerTrial
      ? (hasTrack ? "Keep editing this track, free for 7 days" : "Keep going with a free trial")
      : "Subscribe to get more Message Tokens";
  const message = isSubscriptionActive
    ? "You've run out of Message Tokens. Upgrade your plan to get more monthly messages, or wait for your messages to replenish on your next subscription renewal date."
    : offerTrial
      ? (hasTrack
          ? `You've used your free Message Tokens. Start a 7-day free trial of the ${TRIAL_PLAN} plan and pick up right where you left off: 30 more Message Tokens now, Pro mode and MIDI uploads. $9.99/month after, cancel anytime.`
          : `You've used your free Message Tokens. Try the ${TRIAL_PLAN} plan free for 7 days: 30 more Message Tokens right now, Pro mode and MIDI uploads. Then $9.99/month, cancel anytime from your account.`)
      : "You've run out of Message Tokens. Subscribe to get monthly messages, automatically replenished every billing cycle.";
  const buttonText = isSubscriptionActive
    ? "Upgrade Plan"
    : offerTrial
      ? (isStarting ? "Opening checkout…" : "Start 7-day free trial")
      : "Subscribe";

  const handleClose = () => {
    logEvent('Out of Credits Dialog Closed', { isSubscriptionActive, offerTrial });
    onClose();
  };

  const handleAction = async () => {
    const action = isSubscriptionActive ? 'upgrade' : offerTrial ? 'start_trial' : 'subscribe';
    logEvent('Out of Credits Dialog Action Clicked', { isSubscriptionActive, action });

    if (offerTrial && onPurchase) {
      // Send them straight to Stripe Checkout so they can pick up where they left off
      setIsStarting(true);
      try {
        await onPurchase(TRIAL_PLAN);
      } catch (error) {
        setIsStarting(false);
        navigate('/pricing');
      }
      return;
    }

    navigate('/pricing');
    onClose();
  };

  const handleSeePlans = () => {
    logEvent('Out of Credits Dialog Action Clicked', { isSubscriptionActive, action: 'see_plans' });
    navigate('/pricing');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>{title}</span>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!offerTrial && (
            <div className="bg-[#f05477]/10 border border-[#f05477]/20 rounded-lg p-3 mb-4 hover:bg-[#f05477]/15 transition-colors duration-300">
              <p className="text-sm font-medium text-[#f05477]">
                🎉 Limited Time Offer: Get the Musician Plan for just $9.99/mo (29% off)! Lock in this special price while you can.
              </p>
            </div>
          )}

          <div className="flex items-center space-x-4 mb-4">
            <div className="bg-primary/10 p-3 rounded-full">
              {offerTrial
                ? <Sparkles className="h-6 w-6 text-primary" />
                : <CreditCard className="h-6 w-6 text-primary" />}
            </div>
            <p className="text-muted-foreground flex-1">
              {message}
            </p>
          </div>

          {offerTrial && (
            <p className="text-xs text-muted-foreground">
              Card required. You won't be charged until the trial ends; you'll get an email with the charge date and a link to cancel.
            </p>
          )}

          {onTopUp && (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border p-3">
              <p className="text-sm">
                <span className="font-medium">Not ready for a plan?</span>{' '}
                <span className="text-muted-foreground">{TOPUP_TOKENS} tokens for ${TOPUP_PRICE}, one-off, no subscription.</span>
              </p>
              <Button variant="outline" size="sm" className="flex-shrink-0" onClick={handleTopUp}>
                Get {TOPUP_TOKENS} tokens
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          {offerTrial ? (
            <Button variant="outline" onClick={handleSeePlans}>
              See all plans
            </Button>
          ) : (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleAction}
            disabled={isStarting}
            className="bg-[#f05477] hover:bg-[#d03357] text-white transition-colors duration-300"
          >
            {buttonText}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default OutOfCreditsDialog;
